'use client'

import { useEffect, useMemo, useState } from 'react'

import { useQueries, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  downloadFileByName,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
  Button,
  CurrencyStringDollars,
  FormattedMessage,
  InteractiveIcon,
  Modal,
  SpinnerDots,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import { UnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { ModalContent, ModalFooter } from '@/components/common/modal/ui'
import { FileUpload } from '@/components/FileUpload'
import type { ListingSearchState } from '@/components/listings/search/ListingSearchProvider'
import { SellerCollectsTag } from '@/components/listings/SellerCollectsTag'
import { useBulkBidMutations } from '@/components/listings/shared/BulkBidModal'
import type { BidPlacementResult } from '@/components/listings/shared/BulkBidModal/useBulkBidMutations'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import listingSearchQuery, { getSearchData } from '@/queries/listingSearchQuery'
import type { SearchParams } from '@/queries/listingSearchQuery'

import { ManifestExportCapExceededError } from '../data-loading/fetchAllFilteredListingIds'
import { fetchAllFilteredListings } from '../data-loading/fetchAllFilteredListings'
import { fetchMobileListingIds } from '../data-loading/fetchMobileListingIds'
import { fetchMobileManifestItems } from '../data-loading/fetchMobileManifestItems'
import { buildAfterUploadReportCsv } from './buildAfterUploadReportCsv'
import { buildDownloadableBidSheetCsv } from './buildDownloadableBidSheetCsv'
import type { BadgeTone } from './getPlacedRowDisplay'
import {
  getPlacedRowDisplay,
  ROW_ERROR_MESSAGE_IDS,
} from './getPlacedRowDisplay'
import { groupBidSheetRowsByLot } from './groupBidSheetRowsByLot'
import { parseBidSheetCsv } from './parseBidSheetCsv'
import { isSupersededRow } from './types'
import type {
  BidSheetFileErrorCode,
  ParsedBidSheetRow,
  ValidatedBidSheetRow,
} from './types'
import { validateBidSheetRows } from './validateBidSheetRows'

type Step = 'select' | 'validating' | 'results' | 'placing' | 'placed'

// Matches the chunk size fetchAllResultsById defaults to elsewhere in this
// codebase for the same "look up many IDs" shape — a bid sheet can cover
// hundreds of listings (a full List View export spans many pages), and the
// search endpoint is a single request with no pagination of its own.
const LISTING_LOOKUP_CHUNK_SIZE = 100

// Stable reference for useQuery's default below — a fresh `new Set()` on
// every render would change validatedRows' useMemo dependency every time,
// forcing it to recompute even when nothing actually changed.
const EMPTY_MOBILE_LISTING_IDS: ReadonlySet<string> = new Set()

const StyledModal = styled(Modal)`
  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 43.75rem);
  }
`

const ResultsModal = styled(Modal)`
  @media ${Breakpoints.min.medium} {
    /* 50% larger than StyledModal's 43.75rem, to fit the wider results table. */
    max-width: min(90vw, 65.625rem);
  }
`

const LoadingModal = styled(Modal)`
  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 28rem);
  }
`

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2.5rem 2rem;
  text-align: center;
`

const Ring = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 0.1875rem solid ${Colors.Semantic.Neutral.light_grey};
  border-top-color: ${Colors.Semantic.BStock.default.lightBackground};
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingCaption = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

const FileErrorText = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Error.highlight};
  margin: 0.75rem 0 0;
`

const StepsList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
`

const StepItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StepHeading = styled.h3`
  ${Typography.Title3}
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
`

// A numbered circle stands in for the step illustrations in the design
// mockups — a placeholder until design provides real step visuals, not a
// final visual.
const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: ${Colors.Semantic.BStock.onhover};
  color: ${Colors.Semantic.Neutral.white};
  ${Typography.Overline1}
`

const StepBody = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 2.25rem;
`

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const StatCard = styled.div`
  flex: 1;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.3125rem;
  padding: 0.75rem;
`

const STAT_NUMBER_COLORS: Record<'ready' | 'error' | 'neutral', string> = {
  ready: Colors.Semantic.Success.copy,
  error: Colors.Semantic.Error.highlight,
  neutral: Colors.Semantic.Neutral.dark_grey,
}

const StatNumber = styled.p<{ $tone: 'ready' | 'error' | 'neutral' }>`
  ${Typography.Title2}
  margin: 0;
  color: ${({ $tone }) => STAT_NUMBER_COLORS[$tone]};
`

const StatLabel = styled.p`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

const ResultsTableWrap = styled.div`
  overflow-x: auto;
`

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${Typography.Body3}

  th {
    ${Typography.Overline2}
    color: ${Colors.Semantic.Neutral.dark_grey};
    text-align: left;
    padding: 0.5rem 0.75rem 0.5rem 0;
    border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  }

  td {
    padding: 0.5rem 0.75rem 0.5rem 0;
    border-top: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
    vertical-align: top;
  }

  /* Model (first column) can run long — wrap it instead of stretching the
     table wider, since ResultsTableWrap's overflow-x would otherwise let it
     push every other column out toward the scrollable edge. */
  th:first-child,
  td:first-child {
    max-width: 12rem;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`

const BADGE_TONE_COLORS: Record<BadgeTone, string> = {
  success: Colors.Semantic.Success.copy,
  warning: Colors.Semantic.Warning.onclick,
  error: Colors.Semantic.Error.highlight,
}

const StatusBadge = styled.span<{ $tone: BadgeTone }>`
  display: inline-block;
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: ${Colors.Semantic.Neutral.light_grey};
  color: ${({ $tone }) => BADGE_TONE_COLORS[$tone]};
`

// A plain input rather than the design system's CurrencyInputCents/
// InputBordered — both are built around a floating label, which adds height
// those components don't budget for in a compact table row. The raw string
// typed here flows straight into bidSubmissionRaw, the same field a parsed
// CSV cell lands in, so it's validated by the exact same
// validateBidSheetRows pass either way.
const BidAmountInput = styled.input`
  width: 6.5rem;
  padding: 0.375rem 0.5rem;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  font-family: inherit;
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};

  &:focus {
    outline: none;
    border-color: ${Colors.Semantic.BStock.onhover};
  }
`

// A plain inline text button — Remove/Undo are row-scoped, low-emphasis
// actions that don't warrant a full Button component's size or styling in a
// compact table cell.
const RowActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.5rem;
  ${Typography.Body3}
  color: ${Colors.Semantic.BStock.onhover};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${Colors.Semantic.BStock.default.lightBackground};
  }
`

const PrecedenceNote = styled.p`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0.25rem 0 0;
`

const FILE_ERROR_MESSAGE_IDS: Record<BidSheetFileErrorCode, string> = {
  notCsv: 'Listings.uploadBidSheet.fileError.notCsv',
  empty: 'Listings.uploadBidSheet.fileError.empty',
  missingColumns: 'Listings.uploadBidSheet.fileError.missingColumns',
  readError: 'Listings.uploadBidSheet.fileError.readError',
  unreadableRows: 'Listings.uploadBidSheet.fileError.unreadableRows',
}

const DOWNLOAD_ERROR_MESSAGE_IDS: Record<
  'capExceeded' | 'empty' | 'generic',
  string
> = {
  capExceeded: 'Listings.ListView.export.capExceeded',
  empty: 'Listings.ListView.export.emptyResults',
  generic: 'Listings.uploadBidSheet.step1DownloadError',
}

const TitleCellContent = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

/**
 * The listing title, tagged when the buyer pays that seller directly. Bulk
 * bidders often never open a listing page, so this is the only place the
 * arrangement surfaces in the upload flow.
 */
const TitleCell = ({ row }: { row: ValidatedBidSheetRow }) => {
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  const details = row.listingDetails

  if (!details) return <>—</>

  return (
    <TitleCellContent>
      {details.title}
      {sellerCollectsEnabled && details.sellerCollects && (
        // No sale kind: a bid sheet uploads bids, so every row here is an
        // auction and the tag's default wording is already right.
        <SellerCollectsTag
          sellerName={details.storefrontName}
          data-testid="bid-sheet-seller-collects-tag"
        />
      )}
    </TitleCellContent>
  )
}

// Mirrors the Min Required Bid column's formatting. A 'valid' row already has
// a parsed amount + currency; an 'error' row only has the raw CSV text — e.g.
// invalidAmount rows never had a parseable number to begin with, and
// duplicate/notFound rows never got a listing lookup for a currency. Fall
// back to the raw text (as the buyer typed it) whenever either is missing,
// rather than guessing.
const renderBidSubmissionCell = (row: ValidatedBidSheetRow) => {
  if (row.status === 'valid') {
    return (
      <CurrencyStringDollars
        value={row.amount}
        currency={row.listingDetails.currency}
      />
    )
  }
  const parsedAmount = Number(row.bidSubmissionRaw)
  if (row.listingDetails && Number.isFinite(parsedAmount) && parsedAmount > 0) {
    return (
      <CurrencyStringDollars
        value={parsedAmount}
        currency={row.listingDetails.currency}
      />
    )
  }
  return row.bidSubmissionRaw || '—'
}

// Only meaningful for a 'belowMinimum' row — the only error code that still
// carries a valid parsed amount to subtract from the minimum (every other
// error code either has no amount yet or an unparseable one), and
// validateBidSheetRows only assigns 'belowMinimum' once amount is already
// known to be a finite, positive number less than minimumRequiredBid, so
// this is always positive in practice.
const getShortfall = (
  row: ValidatedBidSheetRow & { status: 'error' }
): number =>
  (row.listingDetails?.minimumRequiredBid ?? 0) - Number(row.bidSubmissionRaw)

type DownloadErrorCode = 'capExceeded' | 'empty' | 'generic'

export const UploadBidSheetModal = ({
  onClose,
  onUploadCompleted,
  selectedListings,
  state,
  storefront,
}: {
  onClose: () => void
  /** Called once bids have actually been placed — not on open, not on
   * upload/validation alone — so the caller can mark a one-time flow (e.g.
   * the entry point's discovery badge) as genuinely used. */
  onUploadCompleted?: () => void
  /** Ticked listings — when empty, Step 1 downloads every listing matching
   * the current filters across all pages instead. */
  selectedListings: CombinedListingSearchResult[]
  state: ListingSearchState
  storefront: StoreFrontDto | undefined
}) => {
  const { formatMessage } = useIntl()
  const { track, trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const isLoggedIn = !!useAccountId()
  const { data: acceptedPolicies, isPending: acceptedPoliciesPending } =
    acceptedPoliciesQuery.useQuery()

  const [step, setStep] = useState<Step>('select')
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<BidSheetFileErrorCode | null>(null)
  const [searchError, setSearchError] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [showUnAuthModal, setShowUnAuthModal] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedBidSheetRow[] | null>(null)
  const [placementResults, setPlacementResults] = useState<
    BidPlacementResult[] | null
  >(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<DownloadErrorCode | null>(
    null
  )
  // Fix-in-place edits made on the review screen, keyed by rowNumber (not
  // listingId — a 'duplicate' row already reports its own error by
  // listingId collision, so keying by listingId here would edit two rows at
  // once). Overlaid onto parsedRows by effectiveParsedRows below, rather
  // than mutating parsedRows directly, so a file re-upload (which replaces
  // parsedRows wholesale) can't leave a stale edit pointing at a rowNumber
  // from the previous file.
  const [editedBidSubmissions, setEditedBidSubmissions] = useState<
    Map<number, string>
  >(new Map())
  // Rows the buyer has dismissed from this upload — an error row they've
  // decided not to fix. Kept visible in the table (never silently dropped),
  // but excluded from the ready/error counts, the Submit gate, and
  // submission itself. Only an error row can be removed; a Ready row has
  // nothing to dismiss.
  const [removedRowNumbers, setRemovedRowNumbers] = useState<Set<number>>(
    new Set()
  )

  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles)
    setFileError(null)
    setSearchError(false)
    setParsedRows(null)
    setEditedBidSubmissions(new Map())
    setRemovedRowNumbers(new Set())
  }

  const handleBidSubmissionEdit = (rowNumber: number, value: string) => {
    setEditedBidSubmissions((prev) => {
      const next = new Map(prev)
      next.set(rowNumber, value)
      return next
    })
  }

  // Fired on blur rather than on every keystroke (onChange, above) — a
  // per-keystroke event would fire dozens of times for one correction.
  // Only fires for a row actually edited this session, so moving focus
  // through an untouched row's input isn't mistaken for an edit.
  const handleBidSubmissionBlur = (rowNumber: number) => {
    if (!editedBidSubmissions.has(rowNumber)) return
    try {
      track('bulk_bid_review_edited', {
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        // Always 'lot_bid' — there's no per-unit bid column on the review
        // screen yet, only the lot-level Bid Submission editable here.
        field: 'lot_bid',
      })
    } catch (error) {
      console.warn(
        '[UploadBidSheetModal] bulk_bid_review_edited tracking failed',
        error
      )
    }
  }

  const handleRemoveRow = (rowNumber: number) => {
    setRemovedRowNumbers((prev) => new Set(prev).add(rowNumber))
  }

  const handleUndoRemoveRow = (rowNumber: number) => {
    setRemovedRowNumbers((prev) => {
      const next = new Set(prev)
      next.delete(rowNumber)
      return next
    })
  }

  const handleSubmit = () => {
    const file = files[0]
    if (!file) return

    setFileError(null)
    setSearchError(false)
    setStep('validating')
    void parseBidSheetCsv(file, {
      listingId: formatMessage({ id: 'Listings.ListView.header.listingId' }),
      bidSubmission: formatMessage({
        id: 'Listings.ListView.header.bidSubmission',
      }),
      perUnitBid: formatMessage({
        id: 'Listings.ListView.header.perUnitBid',
      }),
      units: formatMessage({ id: 'Listings.ListView.header.quantity' }),
    }).then((result) => {
      if (result.ok) {
        setParsedRows(result.rows)
        // A resubmitted After-Upload Report carries the report's own
        // "Result" column alongside Listing ID/Bid Submission — no other
        // upload shape has it, so its presence alone identifies a
        // resubmission without needing a dedicated file marker.
        const isResubmission = Object.keys(
          result.rows[0]?.rawRow ?? {}
        ).includes(
          formatMessage({ id: 'Listings.uploadBidSheet.columnResult' })
        )
        // Only the file-level issues this file's own rows can already prove
        // (a blank Listing ID, or more than one lot-level bid for the same
        // lot) — not the full error set validateBidSheetRows reports, since
        // that needs the listing lookup this event fires ahead of. Grouped
        // the same way validateBidSheetRows groups rows, so the two can't
        // drift on what counts as a lot needing a fix.
        const lotGroups = groupBidSheetRowsByLot(result.rows)
        const lotsNeedingFix = lotGroups.filter(
          (group) => !group.representative.listingId || group.isDuplicateLotBid
        ).length
        const submittedWithPerUnit = result.rows.some(
          (row) => row.perUnitBidRaw !== ''
        )
        try {
          track('bid_sheet_submitted', {
            screenName,
            source: AnalyticsSource.HOME_PORTAL,
            entityType: AnalyticsEntityType.BUYER,
            row_count: result.rows.length,
            // Distinct lots, not raw rows — a manifest lot's per-unit lines
            // are one bid attempt, not one each.
            bids_attempted: lotGroups.length,
            is_resubmission: isResubmission,
            lots_needing_fix: lotsNeedingFix,
            submitted_with_per_unit: submittedWithPerUnit,
          })
        } catch (error) {
          console.warn(
            '[UploadBidSheetModal] bid_sheet_submitted tracking failed',
            error
          )
        }
        if (isResubmission) {
          try {
            track('bulk_bid_corrected_upload', {
              screenName,
              source: AnalyticsSource.HOME_PORTAL,
              entityType: AnalyticsEntityType.BUYER,
              row_count: result.rows.length,
            })
          } catch (error) {
            console.warn(
              '[UploadBidSheetModal] bulk_bid_corrected_upload tracking failed',
              error
            )
          }
        }
      } else {
        // Covers a rejected FileReader read (disk/permission issues, etc.)
        // too — parseBidSheetCsv turns that into { ok: false, errorCode:
        // 'readError' } itself rather than letting the promise reject, so
        // there's a single failure path here instead of a second .catch()
        // duplicating this same reset.
        setParsedRows(null)
        setFileError(result.errorCode)
        setStep('select')
        try {
          track('bid_sheet_error', {
            screenName,
            source: AnalyticsSource.HOME_PORTAL,
            entityType: AnalyticsEntityType.BUYER,
            error_type: result.errorCode,
          })
        } catch (error) {
          console.warn(
            '[UploadBidSheetModal] bid_sheet_error tracking failed',
            error
          )
        }
      }
    })
  }

  const handleReset = () => {
    setFiles([])
    setFileError(null)
    setSearchError(false)
    setSubmitError(false)
    setDownloadError(null)
    setParsedRows(null)
    setPlacementResults(null)
    setEditedBidSubmissions(new Map())
    setRemovedRowNumbers(new Set())
    setStep('select')
  }

  // ResultsModal's closeModal prop (Escape, backdrop, header X) and the
  // footer Close button both resolve to requestCloseResults; Upload Another
  // File resolves to requestResetResults. Both of those discard
  // editedBidSubmissions/removedRowNumbers with no undo, so either prompts
  // first whenever one of those is non-empty, instead of running directly.
  const hasUnsavedReviewChanges =
    editedBidSubmissions.size > 0 || removedRowNumbers.size > 0

  const [pendingDiscardAction, setPendingDiscardAction] = useState<
    'close' | 'reset' | null
  >(null)

  const requestCloseResults = () => {
    if (hasUnsavedReviewChanges) {
      setPendingDiscardAction('close')
    } else {
      onClose()
    }
  }

  const requestResetResults = () => {
    if (hasUnsavedReviewChanges) {
      setPendingDiscardAction('reset')
    } else {
      handleReset()
    }
  }

  const confirmDiscard = () => {
    const action = pendingDiscardAction
    setPendingDiscardAction(null)
    if (action === 'close') onClose()
    else if (action === 'reset') handleReset()
  }

  // Excludes blank Listing IDs (a malformed row, surfaced separately as its
  // own error in validateBidSheetRows) so an empty string never reaches the
  // search request.
  const listingIds = useMemo(
    () =>
      parsedRows
        ? Array.from(
            new Set(parsedRows.map((r) => r.listingId).filter(Boolean))
          )
        : [],
    [parsedRows]
  )

  const listingIdChunks = useMemo(() => {
    const chunks: string[][] = []
    for (let i = 0; i < listingIds.length; i += LISTING_LOOKUP_CHUNK_SIZE) {
      chunks.push(listingIds.slice(i, i + LISTING_LOOKUP_CHUNK_SIZE))
    }
    return chunks
  }, [listingIds])

  const accessToken = useAccessToken()

  // Step 1: downloads a sheet of the ticked listings, or every listing
  // matching the current filters across all pages when none are ticked —
  // the same scoping fetchAllFilteredListings already resolves for the
  // manifest export.
  const handleDownloadSheet = async () => {
    setDownloadError(null)
    setIsDownloading(true)
    try {
      const listings =
        selectedListings.length > 0
          ? selectedListings
          : await fetchAllFilteredListings(accessToken, state, storefront)

      if (listings.length === 0) {
        setDownloadError('empty')
        return
      }

      // Manifest-level rows only apply to mobile-category storefronts;
      // fetchMobileManifestItems returns an empty map for every other case,
      // and buildDownloadableBidSheetCsv falls back to a plain
      // listing-level row whenever a listing has no entry in it. Run
      // alongside fetchMobileListingIds (below) rather than after it — each
      // resolves storefronts independently, and on a mixed-storefront batch
      // that's a fetchStorefrontsById round-trip apiece, so sequencing them
      // would add that second lookup's latency on top of the manifest
      // fan-out instead of overlapping it.
      //
      // A separate, direct storefront-based classification for the title's
      // mobile-vs-general decision — manifestItemsByListingId's own keys
      // aren't a reliable proxy for "this lot is mobile" on their own (it
      // omits a mobile lot with no lotId, or any lot past
      // MAX_MOBILE_MANIFEST_LISTINGS), and the review screen
      // (validateBidSheetRows, via fetchMobileListingIds) already decides
      // the same question this way — sharing the mechanism keeps the
      // downloaded sheet and the re-uploaded review screen from disagreeing
      // on the same lot's title.
      const [manifestItemsByListingId, mobileListingIds] = await Promise.all([
        fetchMobileManifestItems(accessToken, listings, storefront),
        fetchMobileListingIds(accessToken, listings, storefront),
      ])

      const csv = buildDownloadableBidSheetCsv(
        listings,
        {
          title: formatMessage({ id: 'Listings.ListView.header.title' }),
          listingId: formatMessage({
            id: 'Listings.ListView.header.listingId',
          }),
          minRequiredBid: formatMessage({
            id: 'Listings.ListView.header.minRequiredBid',
          }),
          bidSubmission: formatMessage({
            id: 'Listings.ListView.header.bidSubmission',
          }),
          perUnitBid: formatMessage({
            id: 'Listings.ListView.header.perUnitBid',
          }),
          model: formatMessage({ id: 'Listings.ListView.header.model' }),
          grade: formatMessage({ id: 'Listings.ListView.header.bstockGrade' }),
          carrier: formatMessage({ id: 'Listings.ListView.header.carrier' }),
          capacity: formatMessage({ id: 'Listings.ListView.header.capacity' }),
          quantity: formatMessage({ id: 'Listings.ListView.header.quantity' }),
          url: formatMessage({ id: 'Listings.ListView.header.url' }),
        },
        manifestItemsByListingId,
        mobileListingIds
      )
      try {
        track('bulk_bid_sheet_downloaded', {
          screenName,
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
          scope: selectedListings.length > 0 ? 'ticked' : 'filtered',
          listing_count: listings.length,
          sheet_shape:
            manifestItemsByListingId.size > 0 ? 'manifest' : 'listing',
        })
      } catch (error) {
        console.warn(
          '[UploadBidSheetModal] bulk_bid_sheet_downloaded tracking failed',
          error
        )
      }
      downloadFileByName(
        `Bid Sheet ${format(new Date(), "yyyy-MM-dd'T'HHmm")}.csv`,
        csv
      )
    } catch (error) {
      setDownloadError(
        error instanceof ManifestExportCapExceededError
          ? 'capExceeded'
          : 'generic'
      )
    } finally {
      setIsDownloading(false)
    }
  }

  // A bid sheet can cover more listings than the search endpoint can safely
  // take in one request, so this fires one query per LISTING_LOOKUP_CHUNK_SIZE
  // chunk (useQueries, not a loop of listingSearchQuery.useWithOpts calls,
  // since the number of chunks varies between renders) and combines them
  // into the same { data, isError } shape a single query would have had.
  const { data: searchData, isError: searchIsError } = useQueries({
    queries: listingIdChunks.map((chunkIds) => {
      const chunkSearchParams: SearchParams = {
        params: { id: chunkIds, limit: chunkIds.length },
        usePostEndpoint: true,
        fetchFilters: false,
      }
      return {
        queryKey: listingSearchQuery.getKey(accessToken, [chunkSearchParams]),
        queryFn: () => getSearchData(accessToken, chunkSearchParams),
      }
    }),
    combine: (results) => ({
      // Array#every() vacuously returns true for an empty `results` (no
      // lookups needed, e.g. every parsed row had a blank Listing ID) —
      // that's { listings: [] }, not "still loading". An earlier
      // `results.length > 0 &&` guard here broke exactly that case: it fell
      // through to `undefined` with no query left to ever resolve, error,
      // or otherwise flip searchData/searchIsError out of that state.
      data: results.every((r) => r.data !== undefined)
        ? { listings: results.flatMap((r) => r.data.listings) }
        : undefined,
      isError: results.some((r) => r.isError),
    }),
  })

  // Kept separately from validatedRows (rather than only built inside that
  // useMemo) so biddableListings below can reuse the same lookup instead of
  // re-deriving CombinedListingSearchResult instances from searchData.
  const listingsById = useMemo(() => {
    if (!searchData) return new Map<string, CombinedListingSearchResult>()
    return new Map(
      searchData.listings.map((raw) => [
        raw.listingId,
        new CombinedListingSearchResult(raw),
      ])
    )
  }, [searchData])

  // The upload-validate path has no manifest-items map to read a mobile
  // classification off of (unlike the download path, which reuses
  // fetchMobileManifestItems' own), so it resolves one directly — same
  // helper, same storefront-shortcut behavior. Enabled only once
  // listingsById is populated, so this never fires on an empty/initial
  // render; a still-loading state falls back to validateBidSheetRows'
  // default (empty set, i.e. no listing reads as mobile yet).
  const { data: mobileListingIds = EMPTY_MOBILE_LISTING_IDS } = useQuery({
    queryKey: ['mobileListingIds', listingIds, storefront?._id],
    queryFn: () =>
      fetchMobileListingIds(
        accessToken,
        [...listingsById.values()],
        storefront
      ),
    enabled: listingsById.size > 0,
  })

  // Overlays review-screen edits onto the parsed rows before validation.
  // listingIds/listingIdChunks/the search queries above all key off
  // parsedRows rather than this — editing a bid amount never changes which
  // listingId a row points at, so there's nothing for them to re-fetch.
  const effectiveParsedRows = useMemo(() => {
    if (!parsedRows || editedBidSubmissions.size === 0) return parsedRows
    return parsedRows.map((row) =>
      editedBidSubmissions.has(row.rowNumber)
        ? {
            ...row,
            bidSubmissionRaw: editedBidSubmissions.get(row.rowNumber) as string,
          }
        : row
    )
  }, [parsedRows, editedBidSubmissions])

  const validatedRows = useMemo<ValidatedBidSheetRow[] | null>(() => {
    // acceptedPoliciesQuery resolves to {} rather than rejecting on any
    // failure (see getAcceptedPolicies), so it never reaches react-query's
    // 'error' status — only holding on 'pending' matters here. Without this,
    // a row gated on unaccepted terms reads as noActiveAuction for the one
    // render where acceptedPolicies is still undefined, then flips to Ready
    // once the query settles. Folding into the same null-gate as !searchData
    // reuses the existing 'validating' loading step instead of a new state.
    if (!effectiveParsedRows || !searchData || acceptedPoliciesPending)
      return null
    return validateBidSheetRows(effectiveParsedRows, listingsById, {
      editedRowNumbers: new Set(editedBidSubmissions.keys()),
      removedRowNumbers,
      acceptedPolicies,
      mobileListingIds,
    })
  }, [
    effectiveParsedRows,
    searchData,
    acceptedPoliciesPending,
    listingsById,
    editedBidSubmissions,
    removedRowNumbers,
    acceptedPolicies,
    mobileListingIds,
  ])

  // Every validated row minus whatever the buyer has dismissed — the one
  // place removedRowNumbers gets applied, so the Submit gate (readyCount /
  // activeErrorRows below), the actual submission (amountByListingId /
  // biddableListings) and the stats card can never drift out of sync on
  // what counts as "active". A dismissed row must never place a bid, even
  // if it would otherwise validate (e.g. after the buyer removes one of two
  // conflicting lot-level bids, leaving a single, now-valid bid behind) —
  // computing this once, rather than re-checking removedRowNumbers at each
  // call site, is what keeps that guarantee from one place slipping out of
  // step with another.
  const activeValidatedRows = useMemo(
    () =>
      (validatedRows ?? []).filter(
        (row) => !removedRowNumbers.has(row.rowNumber)
      ),
    [validatedRows, removedRowNumbers]
  )

  // The buyer-specified amount for each valid row, looked up by listingId —
  // this is what getAmount below reports instead of the hook's own
  // getUserMinBidAmount default.
  const amountByListingId = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of activeValidatedRows) {
      if (row.status === 'valid') map.set(row.listingId, row.amount)
    }
    return map
  }, [activeValidatedRows])

  const biddableListings = useMemo(
    () =>
      activeValidatedRows
        .filter(
          (row): row is ValidatedBidSheetRow & { status: 'valid' } =>
            row.status === 'valid'
        )
        .map((row) => listingsById.get(row.listingId))
        .filter((l): l is CombinedListingSearchResult => !!l),
    [activeValidatedRows, listingsById]
  )

  const { submit } = useBulkBidMutations(biddableListings, {
    getAmount: (listing) => amountByListingId.get(listing.listingId) ?? 0,
  })

  const handleSubmitBids = () => {
    const readyCount = biddableListings.length
    // Best-effort — a throw here (analytics unavailable, an ad blocker,
    // etc.) must never prevent submit() below from ever being called.
    try {
      trackButtonClicked({
        buttonName: 'bid_sheet_submit_bids_clicked',
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        ready_count: readyCount,
      })
    } catch (error) {
      console.warn(
        '[UploadBidSheetModal] bid_sheet_submit_bids_clicked tracking failed',
        error
      )
    }

    setSubmitError(false)
    setStep('placing')
    const startedAt = Date.now()
    submit().then(
      ({ results }) => {
        setPlacementResults(results)
        setStep('placed')
        onUploadCompleted?.()
        // Best-effort — a throw here (analytics unavailable, an ad
        // blocker, etc.) must never surface as a failed submission; the
        // bids already placed successfully above.
        try {
          const errorTypes = Array.from(
            new Set(
              results
                .filter((r) => !r.success)
                .map((r) => r.errorMessage)
                .filter((message): message is string => !!message)
            )
          )
          track('bid_sheet_processed', {
            screenName,
            source: AnalyticsSource.HOME_PORTAL,
            entityType: AnalyticsEntityType.BUYER,
            bids_placed: results.length,
            bids_winning: results.filter((r) => r.winning).length,
            bids_losing: results.filter((r) => r.success && !r.winning).length,
            bids_errored: results.filter((r) => !r.success && !r.winning)
              .length,
            error_types: errorTypes,
            processing_ms: Date.now() - startedAt,
          })
        } catch (error) {
          console.warn(
            '[UploadBidSheetModal] bid_sheet_processed tracking failed',
            error
          )
        }
      },
      () => {
        setSubmitError(true)
        setStep('results')
      }
    )
  }

  const placementResultsByListingId = useMemo(() => {
    if (!placementResults) return new Map<string, BidPlacementResult>()
    return new Map(placementResults.map((r) => [r.listingId, r]))
  }, [placementResults])

  const handleDownloadReport = () => {
    if (!validatedRows) return

    try {
      trackButtonClicked({
        buttonName: 'after_upload_report_downloaded',
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        delivery: 'button',
        row_count: validatedRows.length,
      })
    } catch (error) {
      console.warn(
        '[UploadBidSheetModal] after_upload_report_downloaded tracking failed',
        error
      )
    }

    const csv = buildAfterUploadReportCsv(
      validatedRows,
      placementResultsByListingId,
      {
        listingId: formatMessage({ id: 'Listings.ListView.header.listingId' }),
        minRequiredBid: formatMessage({
          id: 'Listings.ListView.header.minRequiredBid',
        }),
        bidSubmission: formatMessage({
          id: 'Listings.ListView.header.bidSubmission',
        }),
        perUnitBid: formatMessage({
          id: 'Listings.ListView.header.perUnitBid',
        }),
        submittedBid: formatMessage({
          id: 'Listings.uploadBidSheet.columnSubmittedBid',
        }),
        result: formatMessage({ id: 'Listings.uploadBidSheet.columnResult' }),
        notes: formatMessage({ id: 'Listings.uploadBidSheet.columnNotes' }),
        currentWinningBid: formatMessage({
          id: 'Listings.uploadBidSheet.columnCurrentWinningBid',
        }),
      },
      formatMessage
    )
    downloadFileByName(
      `After-Upload Report ${format(new Date(), "yyyy-MM-dd'T'HHmm")}.csv`,
      csv
    )
  }

  useEffect(() => {
    if (step !== 'validating') return

    if (validatedRows !== null) {
      setStep('results') // oxlint-disable-line react/set-state-in-effect
    } else if (searchIsError) {
      // Without this, a failed listing lookup (network error, 5xx, etc.)
      // leaves validatedRows permanently null and this effect never fires —
      // the buyer would be stuck on the loading dialog with no way out
      // short of closing the whole modal.
      setParsedRows(null)
      setSearchError(true)
      setStep('select')
    }
  }, [step, validatedRows, searchIsError])

  if (step === 'validating') {
    return (
      <LoadingModal closeModal={onClose}>
        <Modal.Header>
          <FormattedMessage id="Listings.uploadBidSheet.validatingTitle" />
        </Modal.Header>
        <ModalContent>
          <LoadingContent>
            <Ring role="status" />
            <LoadingCaption>
              <FormattedMessage id="Listings.uploadBidSheet.validatingCaption" />
            </LoadingCaption>
          </LoadingContent>
        </ModalContent>
      </LoadingModal>
    )
  }

  if (step === 'placing') {
    return (
      <LoadingModal closeModal={onClose}>
        <Modal.Header>
          <FormattedMessage id="Listings.uploadBidSheet.placingTitle" />
        </Modal.Header>
        <ModalContent>
          <LoadingContent>
            <Ring role="status" />
            <LoadingCaption>
              <FormattedMessage id="Listings.uploadBidSheet.placingCaption" />
            </LoadingCaption>
          </LoadingContent>
        </ModalContent>
      </LoadingModal>
    )
  }

  if (step === 'results' && validatedRows) {
    // Same activeValidatedRows a removed row is excluded from everywhere
    // else (amountByListingId, biddableListings) — a dismissed row must
    // never count toward what Submit will actually place, even if it
    // happens to validate (a duplicate-lot-bid group can turn 'valid' once
    // the buyer removes one of the two conflicting rows and the other
    // resolves on its own).
    const readyCount = activeValidatedRows.filter(
      (row) => row.status === 'valid'
    ).length
    // Excludes a superseded row — a conflicting sibling the buyer cleared
    // (not removed), kept visible and editable so it's never silently
    // dropped mid-keystroke, but inert once its lot already has a winner
    // elsewhere in the group.
    const activeErrorRows = activeValidatedRows.filter(
      (row) => row.status === 'error' && !isSupersededRow(row)
    )
    // Counts distinct lots, not rows — a duplicate-lot-bid group reports
    // one error row per conflicting row, but it's still only one lot
    // needing a fix. Not the same number as bid_sheet_submitted's
    // lots_needing_fix — that's a pre-listing-lookup count (missing ID or
    // duplicate groups only, from the just-uploaded file), while this is
    // every blocking error (notFound, ended, belowMinimum, …) minus removed
    // and superseded rows. A blank Listing ID has no lot identity to dedupe
    // by, so each one counts on its own (keyed by rowNumber instead).
    const errorCount = new Set(
      activeErrorRows.map((row) => row.listingId || `__row-${row.rowNumber}`)
    ).size

    // Grouped by currency rather than summed as one number — a bid sheet's
    // listing IDs can span storefronts with different currencies, so adding
    // raw amounts together across currencies would produce a meaningless
    // total. Reads activeValidatedRows, same as readyCount/errorCount above
    // — a removed row must never count toward what Submit will place, and
    // this card shouldn't disagree with that gate.
    const totalBidSpendByCurrency = new Map<string, number>()
    for (const row of activeValidatedRows) {
      if (row.status !== 'valid') continue
      const { currency } = row.listingDetails
      totalBidSpendByCurrency.set(
        currency,
        (totalBidSpendByCurrency.get(currency) ?? 0) + row.amount
      )
    }
    // With no Ready row the loop above never runs, but the stat card is
    // still expected showing $0.00 rather than vanishing — seed it from
    // every currency an error row's listing lookup actually resolved
    // (listingDetails is non-null for every error code except
    // 'missingListingId', 'notFound' and 'unreadable', which never resolve
    // a listing), falling back to USD only when nothing resolved a listing
    // at all.
    if (totalBidSpendByCurrency.size === 0) {
      const resolvedCurrencies = new Set(
        activeValidatedRows
          .map((row) => row.listingDetails?.currency)
          .filter((currency): currency is string => !!currency)
      )
      for (const currency of resolvedCurrencies.size > 0
        ? resolvedCurrencies
        : ['USD']) {
        totalBidSpendByCurrency.set(currency, 0)
      }
    }
    const showCurrencyInLabel = totalBidSpendByCurrency.size > 1

    return (
      <>
        <ResultsModal closeModal={requestCloseResults}>
          <Modal.Header>
            <FormattedMessage id="Listings.uploadBidSheet.resultsTitle" />
          </Modal.Header>

          <ModalContent $size="tall">
            {submitError && (
              <FileErrorText>
                <FormattedMessage id="Listings.uploadBidSheet.submitError" />
              </FileErrorText>
            )}
            <StatsRow>
              <StatCard>
                <StatNumber $tone="ready">{readyCount}</StatNumber>
                <StatLabel>
                  <FormattedMessage id="Listings.uploadBidSheet.readyStat" />
                </StatLabel>
              </StatCard>
              {Array.from(totalBidSpendByCurrency, ([currency, total]) => (
                <StatCard key={currency}>
                  <StatNumber $tone="neutral">
                    <CurrencyStringDollars value={total} currency={currency} />
                  </StatNumber>
                  <StatLabel>
                    {showCurrencyInLabel ? (
                      <FormattedMessage
                        id="Listings.uploadBidSheet.totalBidSpendStatWithCurrency"
                        values={{ currency }}
                      />
                    ) : (
                      <FormattedMessage id="Listings.uploadBidSheet.totalBidSpendStat" />
                    )}
                  </StatLabel>
                </StatCard>
              ))}
              <StatCard>
                <StatNumber $tone="error">{errorCount}</StatNumber>
                <StatLabel>
                  <FormattedMessage id="Listings.uploadBidSheet.reviewErrorsStat" />
                </StatLabel>
              </StatCard>
            </StatsRow>

            <ResultsTableWrap>
              <ResultsTable>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="Listings.uploadBidSheet.columnTitle" />
                    </th>
                    <th>
                      <FormattedMessage id="Listings.uploadBidSheet.columnMinRequiredBid" />
                    </th>
                    <th>
                      <FormattedMessage id="Listings.uploadBidSheet.columnBidSubmission" />
                    </th>
                    <th>
                      <FormattedMessage id="Listings.uploadBidSheet.columnResult" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {validatedRows.map((row) => {
                    const removed = removedRowNumbers.has(row.rowNumber)
                    return (
                      <tr key={row.rowNumber}>
                        <td>
                          <TitleCell row={row} />
                        </td>
                        <td>
                          {row.listingDetails ? (
                            <CurrencyStringDollars
                              value={row.listingDetails.minimumRequiredBid}
                              currency={row.listingDetails.currency}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <BidAmountInput
                            type="text"
                            inputMode="decimal"
                            disabled={removed}
                            value={row.bidSubmissionRaw}
                            onChange={(e) =>
                              handleBidSubmissionEdit(
                                row.rowNumber,
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleBidSubmissionBlur(row.rowNumber)
                            }
                            aria-label={`${formatMessage({
                              id: 'Listings.uploadBidSheet.columnBidSubmission',
                            })} — ${row.listingDetails?.title ?? row.listingId}`}
                          />
                          {row.status === 'valid' &&
                            row.usedLotBidOverPerUnit &&
                            row.perUnitTotal !== null && (
                              <PrecedenceNote>
                                <FormattedMessage
                                  id="Listings.uploadBidSheet.rowPrecedenceNote"
                                  values={{
                                    lotBid: (
                                      <CurrencyStringDollars
                                        value={row.amount}
                                        currency={row.listingDetails.currency}
                                      />
                                    ),
                                    perUnitTotal: (
                                      <CurrencyStringDollars
                                        value={row.perUnitTotal}
                                        currency={row.listingDetails.currency}
                                      />
                                    ),
                                  }}
                                />
                              </PrecedenceNote>
                            )}
                        </td>
                        <td>
                          {removed ? (
                            <>
                              <StatusBadge $tone="warning">
                                <FormattedMessage id="Listings.uploadBidSheet.rowStatusRemoved" />
                              </StatusBadge>
                              <RowActionButton
                                type="button"
                                onClick={() =>
                                  handleUndoRemoveRow(row.rowNumber)
                                }
                                aria-label={`${formatMessage({
                                  id: 'Listings.uploadBidSheet.undoRemoveRow',
                                })} — ${row.listingDetails?.title ?? row.listingId}`}
                              >
                                <FormattedMessage id="Listings.uploadBidSheet.undoRemoveRow" />
                              </RowActionButton>
                            </>
                          ) : isSupersededRow(row) ? (
                            // Its own invalidAmount error doesn't matter — the
                            // lot already has a winner elsewhere in the group —
                            // so this shows neither the red error badge/tooltip
                            // (which would invite retyping and reopen the
                            // conflict) nor a false "Ready". Remove still works,
                            // same as any other dismissible row.
                            <>
                              <StatusBadge $tone="warning">
                                <FormattedMessage id="Listings.uploadBidSheet.rowStatusSuperseded" />
                              </StatusBadge>
                              <RowActionButton
                                type="button"
                                onClick={() => handleRemoveRow(row.rowNumber)}
                                aria-label={`${formatMessage({
                                  id: 'Listings.uploadBidSheet.removeRow',
                                })} — ${row.listingDetails?.title ?? row.listingId}`}
                              >
                                <FormattedMessage id="Listings.uploadBidSheet.removeRow" />
                              </RowActionButton>
                            </>
                          ) : (
                            <>
                              <StatusBadge
                                $tone={
                                  row.status === 'error' ? 'error' : 'success'
                                }
                              >
                                {row.status === 'valid' ? (
                                  <FormattedMessage id="Listings.uploadBidSheet.rowStatusReady" />
                                ) : (
                                  <FormattedMessage id="Listings.uploadBidSheet.rowStatusNeedsFixing" />
                                )}
                              </StatusBadge>
                              {row.status === 'error' && (
                                <InteractiveIcon
                                  size="small"
                                  tooltipProps={{ placement: 'top' }}
                                >
                                  {row.errorCode === 'belowMinimum' &&
                                  row.listingDetails ? (
                                    <FormattedMessage
                                      id="Listings.uploadBidSheet.rowError.belowMinimumWithShortfall"
                                      values={{
                                        shortfall: (
                                          <CurrencyStringDollars
                                            value={getShortfall(row)}
                                            currency={
                                              row.listingDetails.currency
                                            }
                                          />
                                        ),
                                      }}
                                    />
                                  ) : (
                                    <FormattedMessage
                                      id={ROW_ERROR_MESSAGE_IDS[row.errorCode]}
                                    />
                                  )}
                                </InteractiveIcon>
                              )}
                              {row.status === 'error' && (
                                <RowActionButton
                                  type="button"
                                  onClick={() => handleRemoveRow(row.rowNumber)}
                                  aria-label={`${formatMessage({
                                    id: 'Listings.uploadBidSheet.removeRow',
                                  })} — ${row.listingDetails?.title ?? row.listingId}`}
                                >
                                  <FormattedMessage id="Listings.uploadBidSheet.removeRow" />
                                </RowActionButton>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </ResultsTable>
            </ResultsTableWrap>
          </ModalContent>

          <ModalFooter>
            <Modal.Actions alignment="firstOnLeft">
              <Button
                type="button"
                appearance="plainTertiary"
                onClick={requestCloseResults}
              >
                <FormattedMessage id="Listings.uploadBidSheet.close" />
              </Button>
              <Button
                type="button"
                appearance="secondary"
                onClick={requestResetResults}
              >
                <FormattedMessage id="Listings.uploadBidSheet.uploadAnother" />
              </Button>
              <Button
                type="button"
                onClick={
                  isLoggedIn ? handleSubmitBids : () => setShowUnAuthModal(true)
                }
                // Every lot must be Ready or dismissed before Submit enables —
                // errorCount already excludes a removed row, so this is "any
                // lot still unresolved" (readyCount === 0 alone would allow
                // submitting a sheet that's entirely unresolved-but-removed,
                // with nothing actually ready to bid on).
                disabled={isLoggedIn && (readyCount === 0 || errorCount > 0)}
              >
                <FormattedMessage
                  id={
                    isLoggedIn
                      ? 'Listings.uploadBidSheet.submitBidsCount'
                      : 'Listings.uploadBidSheet.signInToBid'
                  }
                  values={{ count: readyCount }}
                />
              </Button>
            </Modal.Actions>
          </ModalFooter>

          {showUnAuthModal && (
            <UnAuthWarningModal
              closeUnAuthModal={() => setShowUnAuthModal(false)}
              reason={formatMessage({
                id: 'Listings.uploadBidSheet.unAuthReason',
              })}
            />
          )}
        </ResultsModal>
        {pendingDiscardAction && (
          <Modal closeModal={() => setPendingDiscardAction(null)}>
            <Modal.Header>
              <FormattedMessage id="Listings.uploadBidSheet.discardConfirm.title" />
            </Modal.Header>
            <Modal.Content>
              <p>
                <FormattedMessage id="Listings.uploadBidSheet.discardConfirm.warning" />
              </p>
              <Modal.Actions alignment="end">
                <Button
                  type="button"
                  appearance="dangerousSecondary"
                  onClick={confirmDiscard}
                >
                  <FormattedMessage id="Listings.uploadBidSheet.discardConfirm.leave" />
                </Button>
                <Button
                  type="button"
                  appearance="primary"
                  onClick={() => setPendingDiscardAction(null)}
                >
                  <FormattedMessage id="Listings.uploadBidSheet.discardConfirm.stay" />
                </Button>
              </Modal.Actions>
            </Modal.Content>
          </Modal>
        )}
      </>
    )
  }

  if (step === 'placed' && validatedRows && placementResults) {
    // winning:true (already holds the top spot) counts as Winning
    // regardless of success — same rule getPlacedRowDisplay already uses
    // for the row badge (see isAlreadyWinning in useBulkBidMutations.ts).
    const winningCount = placementResults.filter((r) => r.winning).length
    const losingCount = placementResults.filter(
      (r) => r.success && !r.winning
    ).length
    // The table below renders every validatedRows entry, not just the ones
    // that reached placement — pre-validation error rows (row.status ===
    // 'error') never appear in placementResults at all, since they were
    // filtered out of biddableListings before useBulkBidMutations ever ran.
    // Without adding them here, this stat could read "0 Errors" while the
    // table still shows red error badges. success:false,winning:true rows
    // are already counted in winningCount above and must not double-count
    // here as errors. A superseded row is excluded — its lot already placed
    // through a different row in the same group (getPlacedRowDisplay reads
    // that row's own outcome for it instead of a spurious Error).
    const errorCount =
      placementResults.filter((r) => !r.success && !r.winning).length +
      validatedRows.filter(
        (row) => row.status === 'error' && !isSupersededRow(row)
      ).length

    return (
      <ResultsModal closeModal={onClose}>
        <Modal.Header>
          <FormattedMessage id="Listings.uploadBidSheet.placedTitle" />
        </Modal.Header>

        <ModalContent $size="tall">
          <StatsRow>
            <StatCard>
              <StatNumber $tone="ready">{winningCount}</StatNumber>
              <StatLabel>
                <FormattedMessage id="Listings.uploadBidSheet.winningStat" />
              </StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber $tone="neutral">{losingCount}</StatNumber>
              <StatLabel>
                <FormattedMessage id="Listings.uploadBidSheet.losingStat" />
              </StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber $tone="error">{errorCount}</StatNumber>
              <StatLabel>
                <FormattedMessage id="Listings.uploadBidSheet.errorsStat" />
              </StatLabel>
            </StatCard>
          </StatsRow>

          <ResultsTableWrap>
            <ResultsTable>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="Listings.uploadBidSheet.columnTitle" />
                  </th>
                  <th>
                    <FormattedMessage id="Listings.uploadBidSheet.columnMinRequiredBid" />
                  </th>
                  <th>
                    <FormattedMessage id="Listings.uploadBidSheet.columnBidSubmission" />
                  </th>
                  <th>
                    <FormattedMessage id="Listings.uploadBidSheet.columnResult" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {validatedRows
                  // A superseded row's lot already placed through a
                  // different row in the same group — the buyer never bid
                  // anything on this one, so showing it here (with a blank
                  // Bid Submission, yet labelled Winning/Losing) would be
                  // misleading. The review table is the one place it still
                  // needs to appear, to stay editable.
                  .filter((row) => !isSupersededRow(row))
                  .map((row) => {
                    const display = getPlacedRowDisplay(
                      row,
                      placementResultsByListingId.get(row.listingId)
                    )
                    const note = display.noteId ? (
                      <FormattedMessage id={display.noteId} />
                    ) : (
                      display.noteFallback
                    )
                    return (
                      <tr key={row.rowNumber}>
                        <td>
                          <TitleCell row={row} />
                        </td>
                        <td>
                          {row.listingDetails ? (
                            <CurrencyStringDollars
                              value={row.listingDetails.minimumRequiredBid}
                              currency={row.listingDetails.currency}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{renderBidSubmissionCell(row)}</td>
                        <td>
                          <StatusBadge $tone={display.tone}>
                            <FormattedMessage id={display.statusId} />
                          </StatusBadge>
                          {note && (
                            <InteractiveIcon
                              size="small"
                              tooltipProps={{ placement: 'top' }}
                            >
                              {note}
                            </InteractiveIcon>
                          )}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </ResultsTable>
          </ResultsTableWrap>
        </ModalContent>

        <ModalFooter>
          <Modal.Actions alignment="end">
            <Button type="button" appearance="secondary" onClick={handleReset}>
              <FormattedMessage id="Listings.uploadBidSheet.uploadAnother" />
            </Button>
            <Button type="button" appearance="secondary" onClick={onClose}>
              <FormattedMessage id="Listings.uploadBidSheet.close" />
            </Button>
            <Button type="button" onClick={handleDownloadReport}>
              <FormattedMessage id="Listings.uploadBidSheet.downloadReport" />
            </Button>
          </Modal.Actions>
        </ModalFooter>
      </ResultsModal>
    )
  }

  return (
    <StyledModal closeModal={onClose}>
      <Modal.Header>
        <FormattedMessage id="Listings.uploadBidSheet.modalTitle" />
      </Modal.Header>

      <ModalContent $size="tall">
        <StepsList>
          <StepItem>
            <StepHeading>
              <StepNumber>1</StepNumber>
              <FormattedMessage id="Listings.uploadBidSheet.step1Title" />
            </StepHeading>
            <StepBody>
              <FormattedMessage id="Listings.uploadBidSheet.step1Description" />
              <div>
                <Button
                  type="button"
                  appearance="secondary"
                  aria-busy={isDownloading}
                  disabled={isDownloading}
                  onClick={() => void handleDownloadSheet()}
                >
                  {isDownloading ? (
                    <SpinnerDots />
                  ) : (
                    <FormattedMessage id="Listings.uploadBidSheet.step1Button" />
                  )}
                </Button>
              </div>
              {downloadError && (
                <FileErrorText>
                  <FormattedMessage
                    id={DOWNLOAD_ERROR_MESSAGE_IDS[downloadError]}
                  />
                </FileErrorText>
              )}
            </StepBody>
          </StepItem>

          <StepItem>
            <StepHeading>
              <StepNumber>2</StepNumber>
              <FormattedMessage id="Listings.uploadBidSheet.step2Title" />
            </StepHeading>
            <StepBody>
              <FormattedMessage id="Listings.uploadBidSheet.step2Description" />
            </StepBody>
          </StepItem>

          <StepItem>
            <StepHeading>
              <StepNumber>3</StepNumber>
              <FormattedMessage id="Listings.uploadBidSheet.step3Title" />
            </StepHeading>
            <StepBody>
              <FileUpload
                accept=".csv"
                files={files}
                onChange={handleFilesChange}
                label={formatMessage({
                  id: 'Listings.uploadBidSheet.dropLabel',
                })}
                description={formatMessage({
                  id: 'Listings.uploadBidSheet.dropSubtext',
                })}
              />

              {fileError && (
                <FileErrorText>
                  <FormattedMessage id={FILE_ERROR_MESSAGE_IDS[fileError]} />
                </FileErrorText>
              )}
              {searchError && (
                <FileErrorText>
                  <FormattedMessage id="Listings.uploadBidSheet.searchError" />
                </FileErrorText>
              )}
            </StepBody>
          </StepItem>
        </StepsList>
      </ModalContent>

      <ModalFooter>
        <Modal.Actions alignment="end">
          <Button type="button" appearance="secondary" onClick={onClose}>
            <FormattedMessage id="Listings.uploadBidSheet.cancel" />
          </Button>
          <Button
            type="button"
            onClick={isLoggedIn ? handleSubmit : () => setShowUnAuthModal(true)}
            disabled={isLoggedIn && files.length === 0}
          >
            <FormattedMessage
              id={
                isLoggedIn
                  ? 'Listings.uploadBidSheet.submit'
                  : 'Listings.uploadBidSheet.signInToBid'
              }
            />
          </Button>
        </Modal.Actions>
      </ModalFooter>

      {showUnAuthModal && (
        <UnAuthWarningModal
          closeUnAuthModal={() => setShowUnAuthModal(false)}
          reason={formatMessage({
            id: 'Listings.uploadBidSheet.unAuthReason',
          })}
        />
      )}
    </StyledModal>
  )
}
